import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Dropdown,
  DatePicker,
  Iconography,
  IconographySizes,
  Modal,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../../../redux";
import { useIsHidStaff } from "../../../../../../../../hooks/useIsHidStaff";
import { Integration } from "../../../../../../../../redux/slices/projects/Types";
import { getIntegrationDetailSagaAction } from "../../../../../../../../redux/saga/projects/saga-actions";
import { getDevelopmentCurrentStatus } from "../../../integration-steps/development";
// 2026-03-10: getRecentHistoryDocumentationSagaAction used to pre-fill admin verification status from previous history entry on resubmit.
import {
  saveValidationDocumentationSagaAction,
  submitValidationDocumentationSagaAction,
  getValidationHistoryDetailSagaAction,
  getValidationHistorySagaAction,
  finalSubmitValidationDocumentationSagaAction,
  getRecentHistoryDocumentationSagaAction,
  getValidationSummarySagaAction,
  exportHistoryValidationReportSagaAction,
  downloadSolutionCertificateSagaAction,
} from "../../../../../../../../redux/saga/validation-documentation/saga-actions";
import { HistoryEntryType } from "../../../../../../../../redux/slices/validation-documentation";
import {
  getUserListSagaAction,
  getUserListHIDSagaAction,
} from "../../../../../../../../redux/saga/user/saga-actions";
import { getEnvironmentDetailsSagaAction } from "../../../../../../../../redux/saga/project-validation/saga-actions";
import CategorySidebar from "../CategorySidebar/index";
import DocumentationTable from "../DocumentationTable/index";
import HistoryNotesTable from "../HistoryNotesTable/index";
import ValidationSummary from "../ValidationSummary/index";
import EnvironmentDetailsModal from "../modals/EnvironmentDetailsModal";
import FilesSection from "../FilesSection/index";
import ConfirmationModal from "../modals/ConfirmationModal";
import { getDocumentationIds } from "../../../../../../../../utils/getDocumentationIds";
import ValidationErrorModal from "../modals/ValidationErrorModal";
import sanitizeHtml from "sanitize-html";
import {
  usePartnerSessionCanAccessModule,
  usePartnerSessionCanEditModule,
  usePartnerSessionModuleReadOnly,
} from "../../../../../../../../hooks/usePartnerSessionPermissions";
import { PARTNER_PORTAL_PERMISSION_KEYS as PP_KEYS } from "../../../../../../../../utils/permissions/partnerPortalPermissionKeys";
import "./ValidationHistoryDetail.scss";

const HID_NOTES_MAX_PLAIN_LENGTH = 2000;

/** Strip RTE HTML before persisting hid_notes (matches partner notes storage expectations). */
const plainHidNotesForApi = (html: string): string => {
  const plain = sanitizeHtml(html, {
    allowedTags: [],
    allowedAttributes: {},
  }).trim();
  if (plain.length <= HID_NOTES_MAX_PLAIN_LENGTH) return plain;
  return plain.slice(0, HID_NOTES_MAX_PLAIN_LENGTH);
};
/** Preserve safe RTE markup before persisting hid_notes. */
const sanitizeHidNotesForApi = (html: string): string =>
  sanitizeHtml(html || "", {
    allowedTags: [
      "p",
      "br",
      "ul",
      "ol",
      "li",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "s",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "a",
    ],
    allowedAttributes: {
      a: ["href", "target", "rel"],
    },
    allowedSchemes: ["http", "https", "mailto", "tel"],
    transformTags: {
      a: sanitizeHtml.simpleTransform("a", {
        target: "_blank",
        rel: "noopener noreferrer",
      }),
    },
  }).trim();

const ValidationHistoryDetail: React.FC = () => {
  const { integrationId, entryId } = useParams<{
    integrationId: string;
    entryId: string;
    projectName?: string;
  }>();
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (integrationId && entryId) {
      dispatch(
        getValidationSummarySagaAction(Number(integrationId), Number(entryId)),
      );
    }
  }, [integrationId, entryId, dispatch]);

  const DownloadSummaryButton = () => (
    <Button
      variant="flat"
      className="documentation-action-link"
      label={
        <span className="documentation-action-link__content">
          Download Certificate
          <Iconography icon="download" size={IconographySizes.Small} />
        </span>
      }
      normalCase={true}
      onClick={handleDownloadCertificate}
      disabled={status.certificateDownloadLoading || !integrationId || !entryId}
    />
  );

  const navigate = useNavigate();
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const isHIDUser = useIsHidStaff();
  const { integrationDetail, projectDetails } = useAppSelector(
    (state) => state.projects,
  );
  // 2026-03-10: recentHistoryDocumentation holds previous history entry's doc so Verification Status can be pre-filled on admin resubmit view.
  const {
    historyDetailDocumentation,
    historyData,
    status,
    submittedBy,
    submittedByAdmin,
    recentHistoryDocumentation,
  } = useAppSelector((state) => state.validation_documentation);
  const { userList, userListHID, fetchedUserListPartnerID } = useAppSelector(
    (state) => state.user,
  );
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const { environmentDetails, historyEnvironmentDetails } = useAppSelector(
    (state) => state.project_validation,
  );

  // Use historyDetailDocumentation for history view
  const documentationData = historyDetailDocumentation;

  // For save operations, check historyDetailLoading (used by save saga when isHistoryView is true)
  // For fetch operations, check historyDetailLoading (used by history detail fetch saga)
  const isSaveLoading = status.historyDetailLoading;

  // Find the integration data from project details
  const { technology, integrationName, integrationData } = useMemo(() => {
    if (!integrationDetail || !integrationId) {
      return {
        technology: "",
        integrationName: "",
        integrationData: null,
      };
    }

    return {
      technology: integrationDetail.technology,
      integrationName: integrationDetail.integration,
      integrationData: integrationDetail,
    };
  }, [integrationDetail, integrationId]);

  const documentationIds = useMemo(
    () =>
      getDocumentationIds(projectDetails, integrationId, integrationDetail),
    [projectDetails, integrationId, integrationDetail]
  );

  // Extract dynamic categories from API response
  const categories = useMemo(() => {
    if (!documentationData || documentationData.length === 0) {
      return [];
    }
    return documentationData.map((cat) => cat.category_name);
  }, [documentationData]);

  const [activeCategory, setActiveCategory] = useState<string>("");
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [isEnvironmentModalOpen, setIsEnvironmentModalOpen] = useState(false);
  const [environmentValidationStatus, setEnvironmentValidationStatus] =
    useState<string | null>(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isValidationErrorModalOpen, setIsValidationErrorModalOpen] =
    useState(false);
  const [
    isEnvironmentVerificationErrorModalOpen,
    setIsEnvironmentVerificationErrorModalOpen,
  ] = useState(false);
  const [pendingCategories, setPendingCategories] = useState<string[]>([]);
  const [documentationFormData, setDocumentationFormData] = useState<
    Record<
      string,
      {
        implementationStatus: string | null;
        verificationStatus: string | null;
        partnerComments: string;
        hidComments: string;
      }
    >
  >({});
  // Ref to track if form data has been initialized to prevent reset on re-renders
  const formDataInitializedRef = useRef(false);
  // Ref to track if user has manually selected validation status to prevent reset
  const validationStatusManuallySetRef = useRef(false);
  // Ref to track if validation history detail has been initially loaded for current entryId
  const validationHistoryDetailLoadedRef = useRef<number | null>(null);
  const [hidNotes, setHidNotes] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toasts, setToasts] = useState<any[]>([]);
  const [adminSubmittedOn, setAdminSubmittedOn] = useState<Date | null>(null);
  const wasLoadingRef = useRef(false);
  const canAccessApproval = usePartnerSessionCanAccessModule(
    PP_KEYS.VALIDATION_APPROVAL
  );
  const canEditApproval = usePartnerSessionCanEditModule(
    PP_KEYS.VALIDATION_APPROVAL
  );
  const approvalReadOnly = usePartnerSessionModuleReadOnly(
    PP_KEYS.VALIDATION_APPROVAL
  );
  const canEditSubmission = usePartnerSessionCanEditModule(
    PP_KEYS.VALIDATION_SUBMISSION
  );
  const submissionReadOnly = usePartnerSessionModuleReadOnly(
    PP_KEYS.VALIDATION_SUBMISSION
  );
   const allowFileMutations = isHIDUser
    ? canEditApproval && !approvalReadOnly
    : canEditSubmission && !submissionReadOnly;

  // Project details are already loaded by parent ProjectDetails (same route layout); we only read from store for isDevelopmentCompleted.

  // Fetch integration detail when integrationId is available
  useEffect(() => {
    if (integrationId) {
      dispatch(getIntegrationDetailSagaAction(Number(integrationId)));
    }
  }, [integrationId, dispatch]);

  // Derive whether Development step is completed (for disabling Save/Submit in Environment Details modal when partner navigated before completing Development)
  const isDevelopmentCompleted = useMemo(() => {
    if (!projectDetails?.technologies || !integrationId) return false;
    const integration = projectDetails.technologies
      .flatMap((tech) => tech.integrations || [])
      .find((int) => int.integration_id === Number(integrationId));
    const status = getDevelopmentCurrentStatus(integration);
    return status === "Completed";
  }, [projectDetails, integrationId]);

  // Fetch history list when integrationId is available
  useEffect(() => {
    if (integrationId) {
      dispatch(getValidationHistorySagaAction(Number(integrationId)));
    }
  }, [integrationId, dispatch]);

  // 2026-03-10: Fetch previous history entry's documentation so admin sees last Verification Status (e.g. Failed) when partner resubmits, instead of "Select".
  useEffect(() => {
    if (!integrationId || !entryId || historyData.length === 0) {
      return;
    }

    const currentDocId = Number(entryId);
    const currentIndex = historyData.findIndex(
      (entry: HistoryEntryType) => entry.documentation_id === currentDocId,
    );
    if (currentIndex === -1) {
      return;
    }

    const previousEntry = historyData[currentIndex + 1];
    if (!previousEntry) {
      return;
    }

    dispatch(
      getRecentHistoryDocumentationSagaAction(
        Number(integrationId),
        previousEntry.documentation_id,
      ),
    );
  }, [integrationId, entryId, historyData, dispatch]);

  // Load user lists if not already loaded
  useEffect(() => {
    if (userListHID.length === 0) {
      dispatch(getUserListHIDSagaAction());
    }

    if (
      partnerDetails?.ID &&
      fetchedUserListPartnerID !== partnerDetails.ID &&
      userList.length === 0
    ) {
      dispatch(getUserListSagaAction(partnerDetails.ID));
    }
  }, [
    dispatch,
    partnerDetails?.ID,
    fetchedUserListPartnerID,
    userList.length,
    userListHID.length,
  ]);

  // Track when loading starts (for save/submit operations)
  useEffect(() => {
    if ((isSaving || isSubmitting) && isSaveLoading) {
      wasLoadingRef.current = true;
    }
  }, [isSaveLoading, isSaving, isSubmitting]);

  // Track save/submit success when loading completes without error
  useEffect(() => {
    // Only show notification if:
    // 1. A save/submit operation is in progress
    // 2. Loading was true at some point (wasLoadingRef.current)
    // 3. Loading has now completed (!isSaveLoading)
    if ((isSaving || isSubmitting) && wasLoadingRef.current && !isSaveLoading) {
      if (!status.historyDetailError) {
        // Operation was successful
        if (isSubmitting) {
          setSubmitSuccess(true);
          setIsConfirmationModalOpen(false);
        }

        // Show success notification
        const successToast = {
          id: Math.floor(Math.random() * 1000),
          title: "",
          message: isSubmitting
            ? "Validation submitted successfully!"
            : "Changes saved successfully!",
          variant: NotificationVariants.Success,
          icon: (
            <Iconography icon="circleCheck" size={IconographySizes.Medium} />
          ),
        };
        setToasts([successToast]);
      } else {
        // Operation failed - show error notification
        const errorToast = {
          id: Math.floor(Math.random() * 1000),
          title: "",
          message:
            status.historyDetailError ||
            (isSubmitting
              ? "Failed to submit validation"
              : "Failed to save changes"),
          variant: NotificationVariants.Error,
          icon: <Iconography icon="ban" size={IconographySizes.Medium} />,
        };
        setToasts([errorToast]);
      }
      setIsSaving(false);
      setIsSubmitting(false);
      wasLoadingRef.current = false; // Reset for next operation
    }
  }, [isSaveLoading, status.historyDetailError, isSaving, isSubmitting]);

  // Fetch history detail documentation when entryId is available
  // Only fetch once when component mounts or when entryId changes
  // This prevents refetching when environment details are verified
  useEffect(() => {
    if (integrationId && entryId) {
      // Only fetch if we haven't loaded this entryId yet or if entryId changed
      const currentEntryId = Number(entryId);
      if (
        validationHistoryDetailLoadedRef.current === null ||
        validationHistoryDetailLoadedRef.current !== currentEntryId
      ) {
        validationHistoryDetailLoadedRef.current = currentEntryId;
        dispatch(
          getValidationHistoryDetailSagaAction(
            Number(integrationId),
            currentEntryId,
          ),
        );
      }
    }
  }, [integrationId, entryId, dispatch]);

  // Fetch environment details when entryId is available (for checking verification status)
  useEffect(() => {
    if (integrationId && entryId) {
      dispatch(
        getEnvironmentDetailsSagaAction({
          integration_id: Number(integrationId),
          documentation_id: Number(entryId),
        }),
      );
    }
  }, [integrationId, entryId, dispatch]);

  // Derive entry status from history list (not from documentation detail)
  type EntryStatus =
    | "Submitted"
    | "Validated - Full approval"
    | "Validated - Conditional approval"
    | "Validation - Failed"
    | string;
  const entryStatus: EntryStatus = useMemo(() => {
    if (entryId && historyData && historyData.length > 0) {
      const historyEntry = historyData.find(
        (entry: HistoryEntryType) => entry.documentation_id === Number(entryId),
      );
      if (historyEntry) {
        return historyEntry.validation_status || "Submitted";
      }
    }
    return "Submitted";
  }, [entryId, historyData]);

  const submittedOn = useMemo(() => {
    if (entryId && historyData && historyData.length > 0) {
      const historyEntry = historyData.find(
        (entry: HistoryEntryType) => entry.documentation_id === Number(entryId),
      );
      if (historyEntry?.created_at) {
        return historyEntry.created_at;
      }
    }
    return null as string | null;
  }, [entryId, historyData]);

  const formatDateToYYYYMMDD = (d: Date | null): string | undefined => {
    if (!d) return undefined;
    // Keep selected calendar day stable across timezone conversions.
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);
  };

  // Determine if fields are editable (HID user + Submitted status + approval edit)
  const isEditable = useMemo(() => {
    return isHIDUser && entryStatus === "Submitted" && canEditApproval;
  }, [isHIDUser, entryStatus, canEditApproval]);

  const showApprovalWorkflow = useMemo(() => {
    return isHIDUser && entryStatus === "Submitted" && canAccessApproval;
  }, [isHIDUser, entryStatus, canAccessApproval]);

  const approvalActionsDisabled = approvalReadOnly || !canEditApproval;

  // Set activeCategory to first category when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  // Initialize HID notes from documentation data
  useEffect(() => {
    if (documentationData && documentationData.length > 0 && !hidNotes) {
      const firstCategory = documentationData[0];
      if (firstCategory.hid_notes) {
        setHidNotes(firstCategory.hid_notes);
      }
    }
  }, [documentationData, hidNotes]);

  // Initialize environmentValidationStatus from documentation detail data
  // Only initialize if user hasn't manually set it
  useEffect(() => {
    if (
      !validationStatusManuallySetRef.current &&
      !environmentValidationStatus &&
      documentationData &&
      documentationData.length > 0
    ) {
      const firstCategory = documentationData[0];
      if (firstCategory.validation_status) {
        // Map API validation_status to dropdown value format
        const reverseStatusMap: Record<string, string> = {
          "Validated - Full approval": "full_approval",
          "Validated - Conditional approval": "conditional_approval",
          "Validation - Failed": "failed",
        };
        const dropdownValue = reverseStatusMap[firstCategory.validation_status];
        if (dropdownValue) {
          setEnvironmentValidationStatus(dropdownValue);
        }
      }
    }
  }, [documentationData, environmentValidationStatus]);

  useEffect(() => {
    if (!documentationData || documentationData.length === 0) return;
    if (adminSubmittedOn) return;
    const rawDate = documentationData[0].admin_submission_date;
    if (!rawDate) return;
    const parsed = /^\d{4}-\d{2}-\d{2}$/.test(rawDate)
      ? new Date(
          Number(rawDate.slice(0, 4)),
          Number(rawDate.slice(5, 7)) - 1,
          Number(rawDate.slice(8, 10)),
        )
      : new Date(rawDate);
    if (!Number.isNaN(parsed.getTime())) {
      setAdminSubmittedOn(parsed);
    }
  }, [documentationData, submittedOn, adminSubmittedOn]);

  const environmentStatusOptions = [
    { label: "Select", value: "" },
    { label: "Validated - Full approval", value: "full_approval" },
    {
      label: "Validated - Conditional approval",
      value: "conditional_approval",
    },
    { label: "Validation - Failed", value: "failed" },
  ];

  const handleCategorySelect = (category: string) => {
    setActiveCategory(category);
  };

  const handleBackToHistory = () => {
    navigate(-1);
  };

  const handleExportValidationReport = () => {
    const numericIntegrationId = Number(integrationId);
    const numericDocumentationId = Number(entryId);

    if (
      !Number.isFinite(numericIntegrationId) ||
      !Number.isFinite(numericDocumentationId) ||
      status.historyReportExportLoading
    ) {
      return;
    }

    dispatch(
      exportHistoryValidationReportSagaAction({
        integration_id: numericIntegrationId,
        documentation_id: numericDocumentationId,
        partner_name: partnerDetails?.partner_name,
	      project_name: projectDetails?.project_name,
      })
    );
  };

  const handleDownloadCertificate = () => {
    const numericIntegrationId = Number(integrationId);
    const numericDocumentationId = Number(entryId);

    if (
      !Number.isFinite(numericIntegrationId) ||
      !Number.isFinite(numericDocumentationId) ||
      status.certificateDownloadLoading
    ) {
      return;
    }

    dispatch(
      downloadSolutionCertificateSagaAction({
        integration_id: numericIntegrationId,
        documentation_id: numericDocumentationId,
        company_name: partnerDetails?.partner_name || "",
      })
    );
  };

  const handleSave = () => {
    if (
      !documentationIds ||
      !integrationId ||
      !loggedUserDetail?.ID ||
      !documentationData ||
      documentationData.length === 0
    ) {
      console.error("Missing required data for save");
      return;
    }

    // Process all categories and their requirements into nested structure
    const categoriesToSave = documentationData.map((category) => {
      const categoryRequirements = category.requirements || [];
      const requirementsToSave = categoryRequirements.map((req) => {
        const rowId = `req-${req.requirement_id}`;
        const formDataForRequirement = documentationFormData[rowId] || {
          implementationStatus: null,
          verificationStatus: null,
          partnerComments: "",
          hidComments: "",
        };

        // Build requirement payload with all current values (full save - send all fields)
        const requirementPayload: {
          requirement_id: number;
          implementation_status?: string;
          verification_status?: string;
          partner_comments?: string;
          hid_comments?: string;
        } = {
          requirement_id: req.requirement_id,
        };

        // Include all fields for full save (use current form values or fallback to API values)
        const implementationStatus =
          formDataForRequirement.implementationStatus ||
          req.implementation_status ||
          "Not implemented";
        const verificationStatus =
          formDataForRequirement.verificationStatus ||
          req.verification_status ||
          "Pending";
        const partnerComments =
          formDataForRequirement.partnerComments || req.partner_comments || "";
        const hidComments =
          formDataForRequirement.hidComments || req.hid_comments || "";

        requirementPayload.implementation_status = implementationStatus;
        requirementPayload.verification_status =
          verificationStatus === "Pass" ? "Completed" : verificationStatus;
        requirementPayload.partner_comments = partnerComments;
        requirementPayload.hid_comments = hidComments;

        return requirementPayload;
      });

      return {
        category_id: category.category_id,
        requirements: requirementsToSave,
      };
    });

    // Build single save payload with all categories
    const savePayload: {
      technology: string;
      integration_type: string;
      integration_id: number;
      user: number;
      partner_notes?: string;
      hid_notes?: string;
      validation_status?: string;
      categories: typeof categoriesToSave;
      isHistoryView?: boolean;
      created_at?: string;
    } = {
      technology: documentationIds.technologyId,
      integration_type: documentationIds.integrationTypeId,
      integration_id: Number(integrationId),
      user: loggedUserDetail.ID,
      categories: categoriesToSave,
      isHistoryView: true,
    };

    // For history view, use notes from state (for HID notes) and documentation data (for partner notes)
    if (documentationData.length > 0) {
      const firstCategory = documentationData[0];
      // Use hidNotes from state (updated via InlineEditor); persist sanitized rich text
      const hidNotesHtml = sanitizeHidNotesForApi(hidNotes);
      if (hidNotesHtml) {
        savePayload.hid_notes = hidNotesHtml;
      }
      // Use partner notes from documentation data
      if (firstCategory.partner_notes) {
        savePayload.partner_notes = firstCategory.partner_notes;
      }
    }

    // Include validation_status for HID users if set
    if (isHIDUser && environmentValidationStatus) {
      // Map dropdown value to API value format
      const statusMap: Record<string, string> = {
        full_approval: "Validated - Full approval",
        conditional_approval: "Validated - Conditional approval",
        failed: "Validation - Failed",
      };
      savePayload.validation_status =
        statusMap[environmentValidationStatus] || environmentValidationStatus;
    }
    const createdAtStr = formatDateToYYYYMMDD(adminSubmittedOn);
    if (createdAtStr) {
      savePayload.created_at = createdAtStr;
    }

    // Set saving state for toast notification tracking
    setIsSaving(true);

    // Dispatch single save action with complete payload
    dispatch(saveValidationDocumentationSagaAction(savePayload));
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  // Check if environment details are verified
  const isEnvironmentDetailsVerified = useMemo(() => {
    const envDetails = environmentDetails || historyEnvironmentDetails;
    return envDetails?.form_status === "Verified";
  }, [environmentDetails, historyEnvironmentDetails]);

  // Validate that all verification statuses are set across all categories
  // Only applies to HID Admin users
  const validateVerificationStatuses = (): {
    isValid: boolean;
    pendingCategories: string[];
  } => {
    // Skip validation for Partner users - only HID Admin needs to verify
    if (!isHIDUser) {
      return { isValid: true, pendingCategories: [] };
    }

    if (!documentationData || documentationData.length === 0) {
      return { isValid: true, pendingCategories: [] };
    }

    const categoriesWithPendingStatus: string[] = [];

    documentationData.forEach((category) => {
      const categoryRequirements = category.requirements || [];
      let hasPendingStatus = false;

      categoryRequirements.forEach((req) => {
        const rowId = `req-${req.requirement_id}`;
        const formDataForRequirement = documentationFormData[rowId];

        // Check if verification status is set (either in form data or API data)
        const verificationStatus =
          formDataForRequirement?.verificationStatus || req.verification_status;

        // Verification status should be "Pass", "Failed", or "Not Applicable", not null, undefined, empty, or "Pending"
        const validVerificationStatuses = [
          "Pass",
          "Failed",
          "Not Applicable",
          "Completed",
        ];
        if (
          !verificationStatus ||
          verificationStatus === "Pending" ||
          (typeof verificationStatus === "string" &&
            verificationStatus.trim() === "") ||
          !validVerificationStatuses.includes(verificationStatus)
        ) {
          hasPendingStatus = true;
        }
      });

      if (hasPendingStatus) {
        categoriesWithPendingStatus.push(category.category_name);
      }
    });

    return {
      isValid: categoriesWithPendingStatus.length === 0,
      pendingCategories: categoriesWithPendingStatus,
    };
  };

  const handleSubmit = () => {
    if (!documentationIds || !integrationId || !loggedUserDetail?.ID) {
      console.error("Missing required data for submit");
      return;
    }

    if (isHIDUser && !adminSubmittedOn) {
      console.error("Submitted on date is required for verification");
      return;
    }

    // Validate that validation_status is set for HID users
    if (isHIDUser && !environmentValidationStatus) {
      console.error("Validation status is required for submission");
      return;
    }

    // Validate that all verification statuses are set
    const validationResult = validateVerificationStatuses();
    if (!validationResult.isValid) {
      setPendingCategories(validationResult.pendingCategories);
      setIsConfirmationModalOpen(false);
      setIsValidationErrorModalOpen(true);
      return;
    }

    // Build save payload with validation_status from dropdown
    const savePayload: {
      technology: string;
      integration_type: string;
      integration_id: number;
      user: number;
      partner_notes?: string;
      hid_notes?: string;
      validation_status?: string;
      categories: Array<{
        category_id: number;
        requirements: Array<{
          requirement_id: number;
          implementation_status?: string;
          verification_status?: string;
          partner_comments?: string;
          hid_comments?: string;
        }>;
      }>;
      isHistoryView?: boolean;
      created_at?: string;
    } = {
      technology: documentationIds.technologyId,
      integration_type: documentationIds.integrationTypeId,
      integration_id: Number(integrationId),
      user: loggedUserDetail.ID,
      isHistoryView: true,
      categories: documentationData
        .map((category) => {
          const categoryRequirements = category.requirements || [];
          const requirementsToSave = categoryRequirements
            .map((req) => {
              const rowId = `req-${req.requirement_id}`;
              const formDataForRequirement = documentationFormData[rowId];

              // If no form data for this requirement, skip it (no changes)
              if (!formDataForRequirement) {
                return null;
              }

              // Get current form values and original API values
              const currentImplementationStatus =
                formDataForRequirement.implementationStatus;
              const currentVerificationStatus =
                formDataForRequirement.verificationStatus;
              const currentPartnerComments =
                formDataForRequirement.partnerComments || "";
              const currentHidComments =
                formDataForRequirement.hidComments || "";

              const originalImplementationStatus =
                req.implementation_status || null;
              const originalVerificationStatus =
                req.verification_status || null;
              const originalPartnerComments = req.partner_comments || "";
              const originalHidComments = req.hid_comments || "";

              // Check if any field has changed
              const hasImplementationStatusChange =
                currentImplementationStatus !== originalImplementationStatus;
              const hasVerificationStatusChange =
                currentVerificationStatus !== originalVerificationStatus;
              const hasPartnerCommentsChange =
                currentPartnerComments !== originalPartnerComments;
              const hasHidCommentsChange =
                currentHidComments !== originalHidComments;

              // Only include requirement if at least one field has changed
              if (
                !hasImplementationStatusChange &&
                !hasVerificationStatusChange &&
                !hasPartnerCommentsChange &&
                !hasHidCommentsChange
              ) {
                return null;
              }

              // Build requirement payload with only changed fields
              const requirementPayload: {
                requirement_id: number;
                implementation_status?: string;
                verification_status?: string;
                partner_comments?: string;
                hid_comments?: string;
              } = {
                requirement_id: req.requirement_id,
              };

              // Only include fields that have changed
              if (hasImplementationStatusChange) {
                requirementPayload.implementation_status =
                  currentImplementationStatus || "Not implemented";
              }
              if (hasVerificationStatusChange) {
                requirementPayload.verification_status =
                  (currentVerificationStatus === "Pass"
                    ? "Completed"
                    : currentVerificationStatus) || "Pending";
              }
              if (hasPartnerCommentsChange) {
                requirementPayload.partner_comments = currentPartnerComments;
              }
              if (hasHidCommentsChange) {
                requirementPayload.hid_comments = currentHidComments;
              }

              return requirementPayload;
            })
            .filter((req): req is NonNullable<typeof req> => req !== null);

          // Only include category if it has at least one changed requirement
          if (requirementsToSave.length === 0) {
            return null;
          }

          return {
            category_id: category.category_id,
            requirements: requirementsToSave,
          };
        })
        .filter((cat): cat is NonNullable<typeof cat> => cat !== null),
    };

    // Add notes from state (for HID notes) and documentation data (for partner notes)
    if (documentationData.length > 0) {
      const firstCategory = documentationData[0];
      // Use hidNotes from state (updated via InlineEditor); persist sanitized rich text
      const hidNotesHtml = sanitizeHidNotesForApi(hidNotes);
      if (hidNotesHtml) {
        savePayload.hid_notes = hidNotesHtml;
      }
      // Use partner notes from documentation data
      if (firstCategory.partner_notes) {
        savePayload.partner_notes = firstCategory.partner_notes;
      }
    }

    // Include validation_status from dropdown for HID users
    if (isHIDUser && environmentValidationStatus) {
      const statusMap: Record<string, string> = {
        full_approval: "Validated - Full approval",
        conditional_approval: "Validated - Conditional approval",
        failed: "Validation - Failed",
      };
      savePayload.validation_status =
        statusMap[environmentValidationStatus] || environmentValidationStatus;
    }
    const createdAtStr = formatDateToYYYYMMDD(adminSubmittedOn);
    if (createdAtStr) {
      savePayload.created_at = createdAtStr;
    }

    // Set submitting state and close confirmation modal
    setIsSubmitting(true);
    setIsConfirmationModalOpen(false);

    // Dispatch submit action - saga will handle save then submit sequentially
    dispatch(
      finalSubmitValidationDocumentationSagaAction({
        integration_id: Number(integrationId),
        user: loggedUserDetail.ID,
        documentation_id: Number(entryId),
        savePayload,
        ...(createdAtStr && { created_at: createdAtStr }),
      }),
    );
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  return (
    <div className="validation-history-detail-container">
      <Notification toasts={toasts} setToasts={setToasts} />

      {/* Main Content Area - Sidebar + Documentation */}
      <div className="main-content-area">
        {/* Category Sidebar */}
        <div className="validation-sidebar">
          <h3 className="sidebar-title">Category</h3>
          <CategorySidebar
            categories={categories}
            activeCategory={activeCategory}
            onCategorySelect={handleCategorySelect}
          />
        </div>

        {/* Documentation Section */}
        <div className="documentation-section">
          <div className="documentation-header">
            <h2>Documentation</h2>
            <div className="action-buttons-container">
              {entryStatus !== "Validation - Failed" && <DownloadSummaryButton />}
              <Button
                variant="flat"
                className="documentation-action-link"
                label="Validation Report"
                normalCase={true}
                onClick={handleExportValidationReport}
                disabled={
                  status.historyReportExportLoading ||
                  !integrationId ||
                  !entryId
                }
              />
              <Button
                variant="flat"
                label="View summary"
                normalCase={true}
                onClick={() => setIsSummaryModalOpen(true)}
              />
              <Button
                variant="primary"
                label={
                  isHIDUser
                    ? "Verify Environment Details"
                    : "View Environment Details"
                }
                normalCase={true}
                onClick={() => setIsEnvironmentModalOpen(true)}
                disabled={isHIDUser && approvalActionsDisabled}
              />
            </div>
          </div>

          <button
            type="button"
            className="back-link"
            onClick={handleBackToHistory}
          >
            <Iconography icon="arrowLeft" size={IconographySizes.Small} />
            <span>BACK TO VALIDATION HISTORY</span>
          </button>

          <div className="documentation-table-area">
            {/* 2026-03-10: recentHistoryDocumentation = previous entry doc so DocumentationTable pre-fills Verification Status (e.g. Failed) on admin resubmit view. */}
            <DocumentationTable
              isHIDUser={isHIDUser}
              activeCategory={activeCategory}
              isHistoryView={true}
              isEditable={isEditable}
              canEditApproval={canEditApproval && !approvalReadOnly}
              canEditSubmission={canEditSubmission && !submissionReadOnly}
              technology={documentationIds?.technologyId}
              integrationType={documentationIds?.integrationTypeId}
              integrationId={integrationId ? Number(integrationId) : undefined}
              onFormDataChange={setDocumentationFormData}
              parentFormData={documentationFormData}
              recentHistoryDocumentation={recentHistoryDocumentation}
            />
          </div>
        </div>
      </div>

      {/* Notes Section - Dynamic notes from documentation data */}
      <HistoryNotesTable
        partnerNotes={
          documentationData && documentationData.length > 0
            ? documentationData[0].partner_notes || null
            : null
        }
        hidNotes={hidNotes || null}
        isHIDUser={isHIDUser}
        isEditable={isEditable}
        submittedBy={
          submittedBy ||
          (documentationData && documentationData.length > 0
            ? documentationData[0].submitted_by
            : null)
        }
        submittedByAdmin={
          submittedByAdmin ||
          (documentationData && documentationData.length > 0
            ? documentationData[0].submitted_by_admin
            : null)
        }
        partnerSubmittedOn={
          documentationData && documentationData.length > 0
            ? documentationData[0].partner_submission_date || submittedOn
            : submittedOn
        }
        hidSubmittedOn={
          adminSubmittedOn
            ? formatDateToYYYYMMDD(adminSubmittedOn)
            : documentationData && documentationData.length > 0
              ? documentationData[0].admin_submission_date || null
              : null
        }
        onHidNotesChange={setHidNotes}
      />

      {/* Files Section */}
      <FilesSection
        isHIDUser={isHIDUser}
        integrationId={integrationId}
        allowFileMutations={allowFileMutations}
      />

      {/* Environment Status - HID Only, visible for history entries */}
      {showApprovalWorkflow && (
        <div className="environment-status-section">
          <label className="environment-status-label">Submitted on</label>
          <DatePicker
            id="admin-submitted-on"
            name="adminSubmittedOn"
            label=""
            format="yyyy-MM-dd"
            placeholder="yyyy-mm-dd"
            theme="dark"
            value={adminSubmittedOn}
            onChange={(date) => {
              if (approvalActionsDisabled) return;
              setAdminSubmittedOn(
                date ? (Array.isArray(date) ? date[0] : date) : null,
              );
            }}
            disabled={approvalActionsDisabled}
            isDisabled={approvalActionsDisabled}
          />
          <label className="environment-status-label">Validation status</label>
          <Dropdown
            id="environment-validation-status"
            label=""
            placeholder="Select"
            options={environmentStatusOptions}
            selected={
              environmentValidationStatus
                ? environmentStatusOptions.find(
                    (opt) => opt.value === environmentValidationStatus,
                  ) || null
                : null
            }
            onSelect={(value) => {
              if (approvalActionsDisabled) return;
              setEnvironmentValidationStatus(value?.value || null);
              // Mark that user has manually selected a value
              validationStatusManuallySetRef.current = true;
            }}
            disabled={approvalActionsDisabled}
            isDisabled={approvalActionsDisabled}
          />
        </div>
      )}

      {/* Footer Actions - HID approval workflow (edit or view-only) */}
      {showApprovalWorkflow && (
        <div className="validation-footer">
          <div className="footer-right-actions">
            <Button
              variant="flat"
              label="Save"
              normalCase={true}
              onClick={handleSave}
              disabled={isSaveLoading || approvalActionsDisabled}
            />
            <Button
              variant="primary"
              label={isHIDUser ? "Verify validations" : "Submit for validation"}
              normalCase={true}
              onClick={() => {
                // Check if environment details are verified when status is "Validated - Full approval"
                if (
                  isHIDUser &&
                  environmentValidationStatus === "full_approval" &&
                  !isEnvironmentDetailsVerified
                ) {
                  setIsEnvironmentVerificationErrorModalOpen(true);
                  return;
                }

                // Validate that all verification statuses are set before showing confirmation modal
                const validationResult = validateVerificationStatuses();
                if (!validationResult.isValid) {
                  setPendingCategories(validationResult.pendingCategories);
                  setIsValidationErrorModalOpen(true);
                  return;
                }
                setIsConfirmationModalOpen(true);
              }}
              disabled={
                isSaveLoading ||
                approvalActionsDisabled ||
                !environmentValidationStatus ||
                !adminSubmittedOn
              }
            />
          </div>
        </div>
      )}

      {isSummaryModalOpen && (
        <Modal
          headerText="Summary"
          width={900}
          onClose={() => setIsSummaryModalOpen(false)}
          className="summary-modal"
        >
          <ValidationSummary
            integrationId={integrationId ? Number(integrationId) : undefined}
            documentationId={entryId ? Number(entryId) : undefined}
          />
        </Modal>
      )}

      {isEnvironmentModalOpen &&
        technology &&
        integrationName &&
        integrationId && (
          <EnvironmentDetailsModal
            isHIDUser={isHIDUser}
            isFromHistoryPage={true}
            onClose={() => {
              setIsEnvironmentModalOpen(false);
              // Refetch environment details to update verification status after a short delay
              // This ensures the modal is fully closed before refetching
              // We explicitly DO NOT refetch validation history detail to preserve form data
              // The form data (documentationFormData) will persist in component state
              setTimeout(() => {
                if (integrationId && entryId) {
                  dispatch(
                    getEnvironmentDetailsSagaAction({
                      integration_id: Number(integrationId),
                      documentation_id: Number(entryId),
                    }),
                  );
                }
              }, 100);
            }}
            technology={technology}
            integration={integrationName}
            integrationId={Number(integrationId)}
            documentationId={entryId ? Number(entryId) : undefined}
            existingData={integrationData}
            disableSaveSubmit={!isDevelopmentCompleted}
            skipProjectDetailsRefetch={true}
          />
        )}

      {isConfirmationModalOpen && (
        <ConfirmationModal
          title="Submit validation"
          message="Are you sure you want to submit validation?"
          confirmLabel="Submit"
          cancelLabel="Cancel"
          onConfirm={handleSubmit}
          onCancel={() => setIsConfirmationModalOpen(false)}
        />
      )}

      {isValidationErrorModalOpen && (
        <ValidationErrorModal
          title="Verification Status Pending"
          message="Verification status is pending from the following category(ies). Please complete all verification statuses before submitting."
          pendingCategories={pendingCategories}
          onClose={() => setIsValidationErrorModalOpen(false)}
        />
      )}

      {isEnvironmentVerificationErrorModalOpen && (
        <Modal
          headerText="Environment Details Verification Required"
          width={500}
          onClose={() => setIsEnvironmentVerificationErrorModalOpen(false)}
          className="confirmation-modal"
        >
          <div className="modal-content">
            <p className="confirmation-message">
              Please verify the environment details first before verifying
              validation.
            </p>
            <div className="modal-footer centered">
              <Button
                variant="flat"
                label="Cancel"
                normalCase={true}
                onClick={() =>
                  setIsEnvironmentVerificationErrorModalOpen(false)
                }
              />
              <Button
                variant="primary"
                label="Verify Environment Details"
                normalCase={true}
                onClick={() => {
                  setIsEnvironmentVerificationErrorModalOpen(false);
                  setIsEnvironmentModalOpen(true);
                }}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ValidationHistoryDetail;
